import React, { useEffect, useState } from "react";
import api from "../lib/api";

function fmtDate(s) {
  if (!s) return "—";
  try {
    return new Date(s).toISOString().slice(0, 10);
  } catch {
    return s;
  }
}

export default function WeeklyReviewsTab() {
  const [list, setList] = useState([]);
  const [autoSend, setAutoSend] = useState(false);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get("/admin/weekly-reviews?limit=200");
        if (!cancelled) {
          setList(data.rows || []);
          setAutoSend(Boolean(data.auto_send_enabled));
          setActive((prev) =>
            prev && (data.rows || []).find((r) => r.review_id === prev.review_id)
              ? prev
              : (data.rows || [])[0] || null
          );
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  const trigger = async () => {
    setRunning(true);
    try {
      await api.post("/admin/weekly-reviews/run");
      setRefreshTick((t) => t + 1);
    } catch (e) {
      setError(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mb-12" data-testid="weekly-reviews-section">
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <p className="label-eyebrow">weekly reviews · phase 1 (generate only)</p>
        <div className="flex items-center gap-3 text-xs">
          <span
            className="px-2 py-1 rounded"
            data-testid="wr-auto-send-flag"
            style={{
              background: autoSend ? "var(--highlight)" : "var(--surface-2)",
              color: autoSend ? "#000" : "var(--text-muted)",
            }}
          >
            AUTO_SEND = {autoSend ? "ON" : "OFF"}
          </span>
          <button
            data-testid="wr-run"
            onClick={trigger}
            disabled={running}
            className="px-3 py-1 rounded bg-white/10 text-neutral-100 hover:bg-white/20 disabled:opacity-50"
          >
            {running ? "running…" : "run now"}
          </button>
          <button
            data-testid="wr-refresh"
            onClick={() => setRefreshTick((t) => t + 1)}
            className="px-3 py-1 rounded text-neutral-500 hover:text-neutral-300"
          >
            ↻
          </button>
        </div>
      </div>

      {loading && (
        <p className="text-neutral-600 text-xs py-4" data-testid="wr-loading">
          Loading…
        </p>
      )}
      {error && (
        <p className="text-neutral-500 text-sm italic py-4" data-testid="wr-error">
          {error}
        </p>
      )}

      {!loading && !error && (
        <div
          className="grid md:grid-cols-3 gap-4"
          data-testid="wr-grid"
        >
          {/* Left: list */}
          <div className="bento-card p-4 md:col-span-1" data-testid="wr-list">
            <p className="label-eyebrow mb-3">{list.length} reviews</p>
            {list.length === 0 ? (
              <p className="text-neutral-500 text-sm italic">
                No reviews yet. Click &quot;run now&quot; to generate.
              </p>
            ) : (
              <ul className="space-y-1 max-h-[480px] overflow-y-auto -mr-2 pr-2">
                {list.map((r) => (
                  <li key={r.review_id}>
                    <button
                      onClick={() => setActive(r)}
                      data-testid={`wr-item-${r.review_id}`}
                      className={`w-full text-left px-3 py-2 rounded text-sm ${
                        active?.review_id === r.review_id
                          ? "bg-white/10"
                          : "hover:bg-white/5"
                      }`}
                    >
                      <div className="text-neutral-200">{r.name || "—"}</div>
                      <div className="text-[11px] text-neutral-600">
                        {r.period_key} · {fmtDate(r.created_at)}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Right: detail */}
          <div className="bento-card p-6 md:col-span-2" data-testid="wr-detail">
            {!active ? (
              <p className="text-neutral-500 text-sm italic">
                Select a review on the left.
              </p>
            ) : (
              <div className="space-y-5">
                <div>
                  <p className="label-eyebrow mb-1">player</p>
                  <p className="text-lg text-neutral-100">{active.name}</p>
                  <p className="text-[11px] text-neutral-600">
                    {active.email || active.phone_number} · {active.period_key}
                  </p>
                </div>

                {/* Activity */}
                <div data-testid="wr-detail-activity">
                  <p className="label-eyebrow mb-2">activity summary</p>
                  <div className="flex gap-6 text-sm">
                    <span>
                      <span className="text-neutral-100">
                        {active.activity?.reports || 0}
                      </span>{" "}
                      <span className="text-neutral-500">reports</span>
                    </span>
                    <span>
                      <span className="text-neutral-100">
                        {active.activity?.successful_video_reviews || 0}
                      </span>{" "}
                      <span className="text-neutral-500">video reviews</span>
                    </span>
                    <span>
                      <span className="text-neutral-100">
                        {active.activity?.inbound_messages || 0}
                      </span>{" "}
                      <span className="text-neutral-500">messages</span>
                    </span>
                  </div>
                </div>

                {/* Confidence */}
                <div data-testid="wr-detail-confidence">
                  <p className="label-eyebrow mb-2">confidence</p>
                  {active.confidence?.available ? (
                    <p className="text-sm text-neutral-200">
                      {active.confidence.avg}/10 avg ({active.confidence.min}–
                      {active.confidence.max}, trend{" "}
                      {{ up: "↑", down: "↓", flat: "→" }[active.confidence.trend] ||
                        "→"}
                      ) across {active.confidence.report_count} report
                      {active.confidence.report_count === 1 ? "" : "s"}.
                    </p>
                  ) : (
                    <p className="text-xs text-neutral-500 italic">
                      No confidence scores this week.
                    </p>
                  )}
                </div>

                {/* Pattern Detection */}
                <div data-testid="wr-detail-pattern">
                  <p className="label-eyebrow mb-2">pattern detection</p>
                  {active.pattern_detection?.patterns?.length ? (
                    <ul className="space-y-1.5 text-sm">
                      {active.pattern_detection.patterns.map((p, i) => (
                        <li
                          key={i}
                          className="text-neutral-300 flex items-baseline justify-between"
                        >
                          <span>
                            <span className="text-[10px] uppercase tracking-widest text-neutral-600 mr-2">
                              {p.category}
                            </span>
                            {p.label}
                          </span>
                          <span className="text-neutral-500 text-xs">
                            ×{p.count} ({p.evidence_kind})
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-neutral-500 italic">
                      No patterns met the 2× threshold this week.
                    </p>
                  )}
                </div>

                {/* Preview message */}
                <div data-testid="wr-detail-preview">
                  <p className="label-eyebrow mb-2">whatsapp preview</p>
                  <pre
                    className="text-xs text-neutral-300 whitespace-pre-wrap p-3 rounded"
                    style={{ background: "var(--surface-2)" }}
                  >
                    {active.message_preview || "—"}
                  </pre>
                </div>

                <p className="text-[10px] text-neutral-700 italic">
                  sent: {active.sent ? "yes" : "no (phase 1)"} · review_id:{" "}
                  {active.review_id}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
